import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Box, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import BoltIcon from "@mui/icons-material/BoltRounded";
import WarnIcon from "@mui/icons-material/WarningAmberRounded";

import Sidebar from "./components/Sidebar";
import TickerStrip from "./components/TickerStrip";
import TopBar from "./components/TopBar";
import Dashboard from "./pages/Dashboard";

// Split per route: the bundle was one 500 kB chunk, so every page paid
// the download cost of all the others before it could render.

/** Owner, 23 Oct 2026: opening OptionEngine lands on the Macro Desk, EMA Agent · Option Sniper first. Once per browser tab,
 *  so the sidebar's Dashboard link still works afterwards. */
function OpenOnMacroDesk({ children }: { children: React.ReactNode }) {
  let first = false;
  try {
    first = !sessionStorage.getItem("oe_landed");
    sessionStorage.setItem("oe_landed", "1");
  } catch { /* storage blocked: just show the dashboard */ }
  return first ? <Navigate to="/macro-desk" replace /> : <>{children}</>;
}

const Positions = lazy(() => import("./pages/Positions"));
const StrategyDetail = lazy(() => import("./pages/StrategyDetail"));
const McxDigger = lazy(() => import("./pages/McxDigger"));
const History = lazy(() => import("./pages/History"));
const Backtest = lazy(() => import("./pages/Backtest"));
const Prebuilt = lazy(() => import("./pages/Prebuilt"));
const MasterOptions = lazy(() => import("./pages/MasterOptions"));
const TrendMaster = lazy(() => import("./pages/TrendMaster"));
const StockTrendMaster = lazy(() => import("./pages/StockTrendMaster"));
const StockPlaybook = lazy(() => import("./pages/StockPlaybook"));
const IndexPlaybook = lazy(() => import("./pages/IndexPlaybook"));
const SwingTrade = lazy(() => import("./pages/SwingTrade"));
const MasterSwing = lazy(() => import("./pages/MasterSwing"));
const CountBackLine = lazy(() => import("./pages/CountBackLine"));
const IntradaySwing = lazy(() => import("./pages/IntradaySwing"));
const MasterProfit = lazy(() => import("./pages/MasterProfit"));
const MasterScalping = lazy(() => import("./pages/MasterScalping"));
const CasEngine = lazy(() => import("./pages/CasEngine"));
const NiftyBlast = lazy(() => import("./pages/NiftyBlast"));
const WebElliott = lazy(() => import("./pages/WebElliott"));
const Jarvis = lazy(() => import("./pages/Jarvis"));
const MacroDesk = lazy(() => import("./pages/MacroDesk"));
import { usePolling } from "./hooks/usePolling";
import { api } from "./api";

const TITLES: Record<string, string> = {
  "/": "Dashboard",
  "/positions": "Positions",
  "/mcx": "MCX Digger",
  "/history": "Trade History",
  "/backtest": "Backtest",
  "/master-options": "MasterOption-Strategy",
  "/trend-master": "TrendMaster",
  "/stock-trend-master": "Stock Trend Master",
  "/swing-trade": "Swing Trade",
  "/master-swing": "Master Swing",
  "/count-back-line": "Count-Back Line",
  "/intraday-swing": "IntradaySwing",
  "/master-profit": "Master-Profit Strategy",
  "/master-scalping": "MasterScalping",
  "/prebuilt": "Pre-Built Algos",
  "/cas": "Closing Auction Session",
  "/blast": "NIFTY Blast Scanner",
  "/web": "Web + Elliott",
  "/jarvis": "Jarvis",
  "/macro-desk": "Macro Desk",
};

export default function App() {
  const { pathname } = useLocation();
  const { data, error, loading, refresh } = usePolling(api.dashboard, 4000);
  // Phone nav. Closed on every route change, otherwise tapping a link leaves
  // the drawer sitting over the page you just navigated to.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [pathname]);

  const title =
    TITLES[pathname] ?? (pathname.startsWith("/strategies") ? "Strategies" : "OptionEngine");

  const control = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn();
      } finally {
        refresh();
      }
    },
    [refresh],
  );

  return (
    <Stack direction="row" sx={{ minHeight: "100vh", position: "relative", zIndex: 1 }}>
      <Sidebar
        open={navOpen}
        onClose={() => setNavOpen(false)}
        mode={data?.mode ?? "OFF"}
        onStart={() => void control(api.start)}
        onStop={() => void control(api.stop)}
        broker={data?.broker}
        detached={data?.detached ?? false}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TopBar
          onMenu={() => setNavOpen(true)}
          title={title}
          mode={data?.mode ?? "OFF"}
          scans={data?.stats.scans ?? 0}
          clock={data?.clock ?? "--:--:--"}
          broker={data?.broker}
        />

        <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1560, mx: "auto" }}>
          {error && (
            <Stack
              direction="row"
              alignItems="center"
              gap={1.2}
              className="panel3d"
              sx={{ p: 1.8, mb: 2, borderLeft: "3px solid var(--red)" }}
            >
              <WarnIcon sx={{ color: "var(--red)", fontSize: 20 }} />
              <Box>
                <Typography sx={{ fontSize: ".84rem", fontWeight: 700 }}>
                  Backend unreachable
                </Typography>
                <Typography sx={{ fontSize: ".72rem", color: "var(--muted)" }}>
                  {error} — is uvicorn running on port 8000?
                </Typography>
              </Box>
            </Stack>
          )}

          {data && (
            <Box sx={{ mb: 2.5 }}>
              <TickerStrip
                quotes={data.ticker ?? data.indices}
                decisions={data.decisions}
              />
            </Box>
          )}

          {loading && !data ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 12, gap: 2 }}>
              <CircularProgress size={30} thickness={5} />
              <Typography sx={{ fontSize: ".82rem", color: "var(--muted)" }}>
                Waking the engine…
              </Typography>
            </Stack>
          ) : data ? (
            <Suspense fallback={
              <Stack alignItems="center" sx={{ py: 8 }}>
                <CircularProgress size={26} thickness={5} />
              </Stack>
            }>
            <Routes>
              <Route path="/" element={<OpenOnMacroDesk><Dashboard data={data} /></OpenOnMacroDesk>} />
              <Route path="/positions" element={<Positions data={data} />} />
              <Route path="/strategies/:key" element={<StrategyDetail data={data} />} />
              <Route path="/mcx" element={<McxDigger />} />
              <Route path="/history" element={<History data={data} />} />
              <Route path="/backtest" element={<Backtest />} />
              <Route path="/prebuilt" element={<Prebuilt />} />
              <Route path="/master-options" element={<MasterOptions />} />
              <Route path="/trend-master" element={<TrendMaster />} />
              <Route path="/stock-trend-master" element={<StockTrendMaster />} />
              <Route path="/stock-playbook" element={<StockPlaybook />} />
              <Route path="/index-playbook" element={<IndexPlaybook />} />
              <Route path="/swing-trade" element={<SwingTrade />} />
              <Route path="/master-swing" element={<MasterSwing />} />
              <Route path="/count-back-line" element={<CountBackLine />} />
              <Route path="/intraday-swing" element={<IntradaySwing />} />
              <Route path="/master-profit" element={<MasterProfit />} />
              <Route path="/master-scalping" element={<MasterScalping />} />
              <Route path="/cas" element={<CasEngine />} />
              <Route path="/blast" element={<NiftyBlast />} />
              <Route path="/web" element={<WebElliott />} />
              <Route path="/jarvis" element={<Jarvis />} />
              <Route path="/macro-desk" element={<MacroDesk />} />
              <Route path="*" element={<Dashboard data={data} />} />
            </Routes>
            </Suspense>
          ) : null}
        </Box>
      </Box>

      {/* close-all: the one destructive control, kept away from the nav */}
      <Tooltip title="Close all open positions at market" placement="left">
        <Box
          onClick={() => void control(api.closeAll)}
          sx={{
            position: "fixed",
            right: { xs: 14, md: 26 },
            bottom: { xs: 14, md: 26 },
            width: { xs: 46, md: 54 },
            height: { xs: 46, md: 54 },
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
            zIndex: 20,
            background: "linear-gradient(150deg,#2563eb,#6d3bf6)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,.4), 0 8px 26px rgba(37,99,235,.5)",
            transition: "transform 180ms cubic-bezier(.22,1,.36,1)",
            "&:hover": { transform: "translateY(-3px) scale(1.05)" },
          }}
        >
          <BoltIcon sx={{ color: "#fff", fontSize: 25 }} />
        </Box>
      </Tooltip>
    </Stack>
  );
}
